import * as Sentry from "@sentry/react";
import { isApiLink } from "lib/Urls";

/** Strip dynamic path segments (UUIDs, numeric IDs) for low-cardinality metric attributes. */
export const normalizeApiPath = (path: string): string =>
  path
    .replace(
      /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      ":id"
    )
    .replace(/\/\d+(?=\/|$)/g, "/:id");

export interface ApiControllerConfig {
  refreshTokenEndpoint: string;
  ignoreTraceForSuccessResponse: RegExp[];
  loggingEnabled: boolean;
  reportError: (
    error: Error | unknown,
    options?: Record<string, unknown>
  ) => void;
  logBreadcrumb: (
    category: string,
    message: string,
    level?: string,
    data?: Record<string, unknown>
  ) => void;
  clientId: string;
  requestsWithoutAuth: string[];
  requestsWithExpected401: string[];
  priorityRequestsWithAuth: string[];
  /**
   * Additional hostnames to treat as 9am API hosts beyond the built-in
   * production allowlist. Used to opt local-dev hosts (e.g. "localhost")
   * into the auth/refresh/dedupe pipeline.
   */
  additionalApiHosts?: string[];
  // Authentication state provider
  authState: {
    accessToken?: string;
    refreshToken?: string;
    authenticationStatus: string;
    refreshAccessToken: () => Promise<void>;
    isAccessTokenExpired?: () => boolean;
  };
}

type RequestQueueOptions = {
  requiresAuth: boolean;
};

type RequestQueueItem =
  | {
      request: () => Promise<unknown>;
      reject: (error: unknown) => void;
      options: RequestQueueOptions;
      running?: boolean;
    }
  | undefined;

export class ApiController {
  config: ApiControllerConfig;

  constructor(config: ApiControllerConfig) {
    this.config = config;
  }

  /** keeps track of how long requests take to load */
  timer: Map<string, number[]> = new Map();

  logTimer = (url: string, time: number): void => {
    if (!this.timer.has(url)) {
      this.timer.set(url, []);
    }
    this.timer.get(url)?.push(time);
  };

  logAverageTimes = (): void => {
    const t: { url: string; average: number; count: number }[] = [];
    this.timer.forEach((times, url) => {
      const average = times.reduce((acc, time) => acc + time, 0) / times.length;
      t.push({ url, average, count: times.length });
    });
    const sorted = t.sort((a, b) => b.average - a.average);
    // eslint-disable-next-line no-console
    console.table(sorted);
  };

  get loggingEnabled(): boolean {
    return this.config.loggingEnabled;
  }

  private readonly log = (message: string, etc?: unknown): void => {
    if (this.loggingEnabled) {
      // eslint-disable-next-line no-console
      console.warn(`[API-C]: ${message}`, etc ?? "");
    }
    this.config.logBreadcrumb("apiCache", message, "info", { etc });
  };

  handleGlobalFetchError = (
    error: Error | unknown,
    urlOpt?: RequestInfo | URL,
    options: Record<string, unknown> = {}
  ): boolean => {
    try {
      const body =
        (
          error as {
            body?: {
              code?: number;
              message?: string;
              data?: Record<string, unknown>;
            };
          }
        ).body ?? {};

      // pass a response error message to error
      if (error instanceof Error && !error.message && body.message) {
        error.message = body.message;
      }

      if (body.data) {
        options.data = body.data;
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      // ignore
    }

    this.config.reportError(error, {
      url: urlOpt,
      options
    });

    return false;
  };

  requestQueue: RequestQueueItem[] = [];
  requestsQueuePriority: RequestQueueItem[] = [];

  // Token refresh coordination
  private isRefreshingToken = false;
  private refreshTokenPromise: Promise<void> | null = null;
  private isQueueHalted = false;

  // In-flight request deduplication
  // Key: `${method}:${url}:${body}`, Value: Promise that resolves to Response
  private inflightRequests = new Map<string, Promise<Response>>();

  private createDedupeKey(
    url: string,
    method: string,
    body?: BodyInit | null
  ): string | null {
    // Skip deduplication for non-string bodies (FormData, ArrayBuffer, Blob, etc.)
    // We can't reliably compare their contents, so each request should be sent
    if (body !== undefined && body !== null && typeof body !== "string") {
      return null;
    }
    const bodyStr = body ?? "";
    return `${method}:${url}:${bodyStr}`;
  }

  /**
   * Invalidate any in-flight request cache for the given API path.
   *
   * Removes matching entries from the in-flight dedupe map so that the NEXT
   * request to this endpoint is forced to issue a fresh HTTP call instead of
   * being deduped against a request that started before the invalidation.
   *
   * Callers that already received the in-flight promise keep it and still
   * resolve normally — only subsequent callers are affected. This is used after
   * a mutation (e.g. creating a lab order) to guarantee the follow-up read does
   * not reuse a stale, already-in-flight list request.
   *
   * @param path A URL path or fragment matched against in-flight request keys,
   *   e.g. "/v1/admin/labs/user1".
   */
  invalidateCache = (path: string): void => {
    this.log("Invalidating cache", { path });
    this.inflightRequests.forEach((_promise, key) => {
      if (key.includes(path)) {
        this.inflightRequests.delete(key);
      }
    });
  };

  /**
   * Reject all queued requests that haven't started executing yet.
   * Called when token refresh fails to ensure all pending requests are properly rejected.
   */
  private rejectAllQueuedRequests = (error: unknown): void => {
    this.log("Rejecting all queued requests due to auth failure");

    // Reject all priority queue requests that haven't started
    this.requestsQueuePriority.forEach((item) => {
      if (item && !item.running) {
        item.reject(error);
      }
    });

    // Reject all normal queue requests that haven't started
    this.requestQueue.forEach((item) => {
      if (item && !item.running) {
        item.reject(error);
      }
    });

    // Clear both queues
    this.requestsQueuePriority = [];
    this.requestQueue = [];
  };

  /**
   * Coordinate token refresh across multiple simultaneous 401 responses.
   * Only one refresh happens at a time - subsequent calls wait for the same promise.
   * Halts queue processing during refresh.
   */
  private coordinatedTokenRefresh = async (): Promise<void> => {
    // If refresh is already in progress, wait for it
    if (this.isRefreshingToken && this.refreshTokenPromise) {
      this.log("Token refresh already in progress, waiting...");
      return this.refreshTokenPromise;
    }

    // Start new refresh
    this.log("Starting coordinated token refresh");
    this.isRefreshingToken = true;
    this.isQueueHalted = true;

    this.refreshTokenPromise = this.config.authState
      .refreshAccessToken()
      .then(() => {
        this.log("Token refresh successful");
      })
      .catch((error: unknown) => {
        this.log("Token refresh failed", error);
        // Reject all queued requests when refresh fails
        this.rejectAllQueuedRequests(
          new Error("Authentication failed - token refresh unsuccessful")
        );
        throw error;
      })
      .finally(() => {
        this.log("Token refresh completed, resuming queue");
        this.isRefreshingToken = false;
        this.refreshTokenPromise = null;
        this.isQueueHalted = false;
        // Resume processing queued requests that were halted during token refresh
        void this.processRequestQueue();
      });

    return this.refreshTokenPromise;
  };

  collectRequestsForBatch = (): RequestQueueItem[] => {
    // Priority requests should be processed even when queue is halted (for token refresh)
    if (this.requestsQueuePriority.length > 0) {
      const priorityRequests = this.requestsQueuePriority.filter(
        (request) => !request?.running
      );
      return priorityRequests;
    }

    // If queue is halted, don't process normal requests
    if (this.isQueueHalted) {
      return [];
    }

    // only return "normal" requests if user is authenticated
    const requestsRequireAuth =
      this.config.authState.authenticationStatus === "authenticated"
        ? this.requestQueue
        : [];

    // filter out requests that are already running
    const requests = requestsRequireAuth.filter((request) => !request?.running);

    return requests;
  };

  processRequestQueue = async (): Promise<void> => {
    // stop if priority request is running
    const priorityRequestIsRunning = this.requestsQueuePriority.some(
      (request) => request?.running
    );
    if (priorityRequestIsRunning) {
      this.log("Priority Request is already running");
      return;
    }

    // Pre-flight check: if access token is expired, refresh it BEFORE sending any requests
    // This prevents bursts of 401 errors when many requests are queued
    const hasAuthRequests = this.requestQueue.some(
      (item) => item?.options.requiresAuth && !item.running
    );
    const isTokenExpired = this.config.authState.isAccessTokenExpired?.();
    // Only attempt a pre-flight refresh when a refresh is actually possible.
    // With no refresh token (e.g. logged out), refreshAccessToken resolves
    // immediately without progress and coordinatedTokenRefresh's finally
    // re-pumps the queue; an expired token plus a still-queued auth request
    // then re-enters this branch every tick, spinning into an infinite loop
    // that freezes the tab. Skipping here lets the request hold until auth
    // completes (handleAuthStatusChange re-pumps) instead of looping.
    const canRefresh = Boolean(this.config.authState.refreshToken);

    if (
      hasAuthRequests &&
      isTokenExpired &&
      canRefresh &&
      !this.isRefreshingToken
    ) {
      this.log(
        "Access token expired before processing queue, refreshing first"
      );
      try {
        await this.coordinatedTokenRefresh();
        // coordinatedTokenRefresh's finally block already resumed the queue
        return;
      } catch {
        // Token refresh failed - coordinatedTokenRefresh already handles cleanup
        return;
      }
    }

    const batchPromise = Promise.all(
      this.collectRequestsForBatch().map(this.processSingleRequest)
    );

    await batchPromise;

    if (this.collectRequestsForBatch().length > 0) {
      void this.processRequestQueue();
    }
  };

  processSingleRequest = async (request: RequestQueueItem): Promise<void> => {
    if (!request || request.running) {
      return;
    }
    request.running = true;

    try {
      await request.request();
    } catch (error) {
      this.handleGlobalFetchError(error);
    } finally {
      // remove request from queue
      const inPriorityQueue = this.requestsQueuePriority.includes(request);
      if (inPriorityQueue) {
        this.requestsQueuePriority = this.requestsQueuePriority.filter(
          (item) => item !== request
        );
      } else {
        this.requestQueue = this.requestQueue.filter(
          (item) => item !== request
        );
      }
    }
  };

  patchRequestOptions = (
    requestInit?: RequestInit,
    options?: {
      requiresAuth?: boolean;
      url: string;
    }
  ): RequestInit | undefined => {
    if (!requestInit) {
      return;
    }
    const { requiresAuth = true, url = "" } = options ?? {};
    const patchedOptions = { ...requestInit };

    const headers = new Headers(requestInit.headers);
    headers.set("X-NineamHealth-Client", this.config.clientId);

    let token = `Bearer ${this.config.authState.accessToken ?? ""}`;

    // use refresh token for refresh token requests
    if (url.endsWith(this.config.refreshTokenEndpoint)) {
      token = `Bearer ${this.config.authState.refreshToken ?? ""}`;
    }

    if (requiresAuth) {
      // add Authorization header
      headers.set("Authorization", token);
    } else {
      // remove Authorization header
      headers.delete("Authorization");
    }

    patchedOptions.headers = headers;

    return patchedOptions;
  };

  logTrace = (url: string, res: Response): void => {
    try {
      const traceId = res.headers.get("X-NineamHealth-TraceId");
      const resCode = res.status;

      const isErrorCode = resCode >= 400;
      if (
        !isErrorCode &&
        this.config.ignoreTraceForSuccessResponse.some((re) => re.test(url))
      ) {
        return;
      }

      if (traceId) {
        this.config.logBreadcrumb(
          "traceId",
          `${resCode} | ${url} | ${traceId}`,
          "log"
        );
      }
    } catch (e) {
      this.config.logBreadcrumb("error", "Error logging trace", "error", { e });
    }
  };

  /**
   * Patch the response object to allow for easy access to the JSON response
   * normally you can only access the response body once the response is consumed,
   * this patch allows you to access the response body for JSON multiple times
   */
  patchResponse = async (res: Response): Promise<void> => {
    const contentType = res.headers.get("content-type");
    if (!res.ok || contentType !== "application/json") {
      return;
    }

    const resJson = await res.json();

    Object.defineProperty(res, "json", {
      value: () => Promise.resolve(resJson),
      writable: true
    });
  };

  patchGlobalFetch = (): void => {
    const tmpFetch = globalThis.fetch;
    globalThis.fetch = (async (url, options) => {
      this.log(options?.method ?? "GET", url);

      const urlObj = new URL(url as string);
      const urlIsNineAmHealth =
        isApiLink(urlObj.toString()) ||
        (this.config.additionalApiHosts?.includes(urlObj.hostname) ?? false);

      // For non-9am URLs, bypass all custom logic
      if (!urlIsNineAmHealth) {
        return tmpFetch(url as Request, options);
      }

      // Check for in-flight duplicate request (skip for non-string bodies like FormData)
      const dedupeKey = this.createDedupeKey(
        urlObj.toString(),
        options?.method ?? "GET",
        options?.body
      );

      if (dedupeKey) {
        const existingRequest = this.inflightRequests.get(dedupeKey);
        if (existingRequest) {
          this.log("Deduping request, reusing in-flight", {
            url: urlObj.pathname
          });
          return existingRequest;
        }
      }

      // Create the request promise
      const requestPromise = new Promise<Response>((appResolve, appReject) => {
        const urlPathname = urlObj.pathname;
        const queryString = urlObj.search;

        const isRequestWithoutAuth = this.config.requestsWithoutAuth.some(
          (requestPath) => urlPathname.includes(requestPath)
        );
        const isPriorityRequest =
          isRequestWithoutAuth ||
          this.config.priorityRequestsWithAuth.some((requestPath) =>
            urlPathname.includes(requestPath)
          );

        const requiresAuth = !isRequestWithoutAuth;
        let failedWith401 = false; // Track if this request already failed with 401

        const request = async () => {
          return new Promise((resolveRequest, rejectRequest) => {
            const startTime = Date.now();
            const method = options?.method ?? "GET";
            const endTimer = (statusCode?: number) => {
              const time = Date.now() - startTime;
              const pathKey = `[${method}] ${urlPathname}${queryString}`;
              this.logTimer(pathKey, time);
              const endpoint = normalizeApiPath(urlPathname);
              Sentry.metrics.distribution("api.request.duration", time, {
                unit: "millisecond",
                attributes: { method, endpoint }
              });
              if (statusCode !== undefined && statusCode >= 400) {
                Sentry.metrics.count("api.request.error", 1, {
                  attributes: { method, endpoint, status: String(statusCode) }
                });
              }
            };

            const reject = (error: unknown) => {
              rejectRequest(error);
              appReject(error);
              endTimer();
            };

            const resolve = (res: Response) => {
              this.patchResponse(res)
                .then(() => {
                  resolveRequest(res);
                  appResolve(res);
                  endTimer(res.status);
                })
                .catch((error: unknown) => {
                  reject(error);
                });
            };

            // add access token to request
            const patchedOptions = this.patchRequestOptions(options, {
              requiresAuth,
              url: urlObj.toString()
            });

            this.config.logBreadcrumb(
              "api",
              `Request: "${urlPathname}". Method: "${options?.method}"`,
              "log"
            );

            // initial attempt to fetch
            void tmpFetch(url as Request, patchedOptions)
              .then((res) => {
                this.logTrace(urlPathname, res);
                // resolve if request succeeded
                if (res.ok) {
                  resolve(res);
                  return;
                }

                const isExpected401 = this.config.requestsWithExpected401.some(
                  (requestPath) => urlPathname.includes(requestPath)
                );
                const isRefreshEndpoint = urlObj.pathname.endsWith(
                  this.config.refreshTokenEndpoint
                );

                if (
                  isExpected401 ||
                  isRequestWithoutAuth ||
                  isRefreshEndpoint
                ) {
                  resolve(res);
                  return;
                }

                // if request failed with 401, refresh access token and try again
                if (res.status === 401) {
                  // Check if this request already failed with 401 before
                  if (failedWith401) {
                    this.log(
                      "Request failed with 401 twice (after token refresh), rejecting",
                      { url: urlPathname }
                    );
                    reject(
                      new Error(
                        "Request failed with 401 after token refresh - possible invalid credentials"
                      )
                    );
                    return;
                  }

                  // Mark that this request failed with 401
                  failedWith401 = true;

                  // Coordinate token refresh - only one refresh happens even if multiple requests hit 401
                  // This halts the queue and waits for refresh to complete
                  void this.coordinatedTokenRefresh()
                    .then(() => {
                      this.log("Retrying request after token refresh", {
                        url: urlPathname,
                        currentToken: `${this.config.authState.accessToken?.substring(
                          0,
                          20
                        )}...`
                      });

                      // Token refreshed successfully, retry this request with new token
                      const patchedOptionsV2 = this.patchRequestOptions(
                        options,
                        {
                          requiresAuth,
                          url: urlObj.toString()
                        }
                      );

                      // Retry the request with new access token
                      void tmpFetch(url as Request, patchedOptionsV2)
                        .then((resAfterRefresh) => {
                          // Check if retry also got 401 - means token refresh didn't help
                          if (resAfterRefresh.status === 401) {
                            this.log(
                              "Retry after token refresh still got 401",
                              { url: urlPathname }
                            );
                            reject(
                              new Error(
                                "Request still unauthorized after token refresh"
                              )
                            );
                            return;
                          }
                          resolve(resAfterRefresh);
                        })
                        .catch((error: unknown) => {
                          reject(error);
                        });
                    })
                    .catch((refreshError: unknown) => {
                      // Token refresh failed - authState will handle logout and cleanup
                      reject(refreshError);
                    });
                } else {
                  // reject for other errors
                  resolve(res);
                  return;
                }
              })
              .catch((error: unknown) => {
                // reject for an initial attempt
                reject(error);
              });
          });
        };

        this.log("adding request to queue", url);
        if (isPriorityRequest) {
          this.requestsQueuePriority.push({
            request,
            reject: appReject,
            options: { requiresAuth }
          });
        } else {
          this.requestQueue.push({
            request,
            reject: appReject,
            options: { requiresAuth }
          });
        }

        void this.processRequestQueue();
      });

      // Store for deduplication (only if we have a valid dedupe key)
      if (dedupeKey) {
        this.inflightRequests.set(dedupeKey, requestPromise);

        // Clean up after request completes (success or failure)
        // Note: We use .then/.catch instead of .finally to avoid unhandled rejection
        // when the promise rejects (finally passes through rejections)
        // Only delete if the stored promise is still THIS one — invalidateCache may
        // have evicted it and a newer request may now own the key.
        const clearOwnEntry = () => {
          if (this.inflightRequests.get(dedupeKey) === requestPromise) {
            this.inflightRequests.delete(dedupeKey);
          }
        };
        requestPromise.then(clearOwnEntry).catch(clearOwnEntry);
      }

      return requestPromise;
    }) as typeof globalThis.fetch;
  };

  requestIncludeResponseHeaders = async <T>(
    method: () => Promise<T>
  ): Promise<{
    body: T;
    headers: Headers;
  }> => {
    let headers = new Headers();

    // mock the global fetch method
    const tmpFetch = globalThis.fetch;
    globalThis.fetch = (async (url, options) => {
      // run the request (do not wait for response)
      const requestPromise = tmpFetch(url as Request, options);
      // reset mock
      globalThis.fetch = tmpFetch;
      const response = await requestPromise;
      // eslint-disable-next-line @typescript-eslint/prefer-destructuring
      headers = response.headers;
      return response;
    }) as typeof globalThis.fetch;

    const apiResponse = await method();

    return {
      body: apiResponse,
      headers
    };
  };

  handleAuthStatusChange = (
    status: typeof this.config.authState.authenticationStatus
  ): void => {
    this.log("handleAuthStatusChange", status);
    if (status === "authenticated") {
      this.requestsQueuePriority = [];
      void this.processRequestQueue();
    }
  };
}
